import type { UseQueryResult } from '@tanstack/react-query';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useEffect } from 'react';
import { useDebounceValue } from 'usehooks-ts';

import { ApplicationContext } from '~/shared/context/context';
import type {
    BaseSkill,
    Skill,
    SkillShort,
    SkillsDetectionRequest,
    SkillsDetectionRequestDocType,
    SkillsOptions,
    SkillsPresets,
} from '~/shared/data-contract/skill';
import {
    baseSkillArraySchema,
    baseSkillSchema,
    skillShortSchema,
    skillsDetectionResponseTransformedSchema,
    skillsOptionsSchema,
    skillsPresetsSchema,
} from '~/shared/data-contract/skill';
import type { Pathname } from '~/shared/http-fetcher/http-fetcher';
import { updateOnUserSkillsMutationKey } from '~/shared/query-hooks/hard-soft-skills';

const useSkillsOptions = (): UseQueryResult<SkillsOptions> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const pathname: Pathname = '/skills/options';

    return useQuery<SkillsOptions>({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(skillsOptionsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET'],
    });
};

export const useSkillPresetsQuery = (): UseQueryResult<SkillsPresets> => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { language } = applicationState;
    const pathname: Pathname = '/skills/presets';

    return useQuery<SkillsPresets>({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(skillsPresetsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', language.code],
    });
};

const skillSearchRootQueryKey = 'skill-search';
const debounceSearchQueryMs = 200;
const skillCharsMinimum = 1;
const searchLimitDefault = 5;

export const useSearchSkillsQuery = (
    searchTerm: string,
    type: readonly Skill['skillType'][],
    searchLimit?: number,
    options: { readonly allowEmptySearch?: boolean } = {},
): UseQueryResult<readonly BaseSkill[]> => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { language } = applicationState;
    const queryClient = useQueryClient();
    const pathname: Pathname = '/skills';
    const [searchTermDebounced] = useDebounceValue(searchTerm, debounceSearchQueryMs);

    // biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration
    useEffect(() => {
        queryClient.cancelQueries({ queryKey: [skillSearchRootQueryKey] });
    }, [queryClient, searchTerm]);

    const searchParams = {
        limit: searchLimit ?? searchLimitDefault,
        searchTerm: searchTermDebounced,
        type: type.join(','),
    };

    return useQuery<readonly SkillShort[]>({
        enabled: options.allowEmptySearch === true || searchTermDebounced.length >= skillCharsMinimum,
        placeholderData: (previousData) => (searchTerm === searchTermDebounced ? previousData : undefined),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(baseSkillSchema.array(), {
                pathname,
                requestOptions: {
                    searchParams,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [skillSearchRootQueryKey, pathname, 'GET', searchParams, language.code],
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        throwOnError: false,
    });
};

const skillStaleTimeMs = 300_000; // 5 mins

export const useManySkillsQuery = ({
    enabled,
    skillIds,
}: {
    readonly enabled: boolean;
    readonly skillIds: readonly string[];
}): UseQueryResult<readonly BaseSkill[]> => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { language } = applicationState;
    const pathname = '/skills/info';

    const ids = new URLSearchParams({
        skillIds: String(skillIds),
    });

    const requestOptions = {
        searchParams: ids,
    };

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(baseSkillSchema.array(), {
                pathname,
                requestOptions,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: ['GET', skillShortSchema, language, requestOptions, pathname, skillIds],
    });
};

type UserSkillsPermissions = {
    readonly canUserGetDetectedSkills: boolean;
    readonly canUserGetRelatedSkills: boolean;
    readonly isFetchingPermissions: boolean;
};

export const useUserSkillsPermissions = (): UserSkillsPermissions => {
    const skillsOptionsQuery = useSkillsOptions();

    const canUserGetRelatedSkills = skillsOptionsQuery.data?.canReadRelatedSkills ?? false;
    const canUserGetDetectedSkills = skillsOptionsQuery.data?.canDetectSkills ?? false;
    const isFetchingPermissions = skillsOptionsQuery.isFetching;

    return {
        canUserGetDetectedSkills,
        canUserGetRelatedSkills,
        isFetchingPermissions,
    };
};

const debounceRecommendationInvalidationTimeMs = 3000;

export const useSkillRecommendationsQuery = (
    initialSkills: readonly SkillShort[],
    limits: {
        readonly hardSkillsLimit: number;
        readonly languageSkillsLimit: number;
        readonly softSkillsLimit: number;
    } = {
        hardSkillsLimit: 10,
        languageSkillsLimit: 2,
        softSkillsLimit: 10,
    },
): UseQueryResult<readonly BaseSkill[], Error> => {
    const [skillsForRecommendationSearch] = useDebounceValue(initialSkills, debounceRecommendationInvalidationTimeMs);
    const { canUserGetRelatedSkills } = useUserSkillsPermissions();
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { language } = applicationState;

    const recommendationRequest = {
        ...limits,
        skills: skillsForRecommendationSearch.map(({ id, level }) => ({
            id,
            level: level ?? 0,
        })),
    };

    const pathname: Pathname = `/skills/related`;

    return useQuery<readonly BaseSkill[], Error>({
        enabled: canUserGetRelatedSkills,
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.postJson(baseSkillArraySchema, {
                pathname,
                payload: recommendationRequest,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'POST', recommendationRequest, language.code],
        staleTime: skillStaleTimeMs,
    });
};

const minLengthForContent = 2;

export const useSkillsDetectionQuery = (
    docType: SkillsDetectionRequestDocType,
    requestData: SkillsDetectionRequest,
    automated: boolean = true,
    options: { readonly includeProficienciesSearchParam?: boolean } = {},
): UseQueryResult<readonly SkillShort[]> => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { canUserGetDetectedSkills } = useUserSkillsPermissions();
    const { language: appLanguage } = applicationState;
    const pathname = '/skills/detection';

    const [debouncedPayload] = useDebounceValue(requestData, debounceRecommendationInvalidationTimeMs);
    const payload = automated ? debouncedPayload : requestData;

    const hasAnyValues = [payload.title ?? '', payload.text].some((value) => value.length >= minLengthForContent);

    const isDocTypeJob = docType === 'job';
    const searchParams =
        options.includeProficienciesSearchParam === false
            ? { docType }
            : { docType, includeProficiencies: isDocTypeJob };

    return useQuery({
        enabled: automated && canUserGetDetectedSkills && hasAnyValues,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.postJson(skillsDetectionResponseTransformedSchema, {
                pathname,
                payload,
                requestOptions: {
                    searchParams,
                    timeout: 60_000,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'POST', appLanguage.code, payload, docType, searchParams],
        staleTime: skillStaleTimeMs,
    });
};

/** Refetches every query that opted into updates when the user's own skills change. */
export const useInvalidateUserSkillsDependents = (): (() => void) => {
    const queryClient = useQueryClient();

    return useCallback(() => {
        queryClient.invalidateQueries({
            predicate: (query) => query.queryKey.includes(updateOnUserSkillsMutationKey),
        });
    }, [queryClient]);
};
